import { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bump CACHE_VERSION when the merge schema/shape changes to invalidate stale caches.
const CACHE_VERSION = 2;
// 1 hour — short enough that admin edits show up reliably after one refresh,
// long enough to keep the no-flash boot benefit during a normal browsing
// session. Previously was 7 days, which masked admin changes for too long.
const CACHE_TTL_MS = 60 * 60 * 1000;
const cacheKey = (pageType, pageId) => `cms:v${CACHE_VERSION}:${pageType}:${pageId}`;

const readCache = (pageType, pageId) => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(cacheKey(pageType, pageId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.savedAt || Date.now() - parsed.savedAt > CACHE_TTL_MS) return null;
    return parsed.value || null;
  } catch {
    return null;
  }
};

const writeCache = (pageType, pageId, value) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      cacheKey(pageType, pageId),
      JSON.stringify({ savedAt: Date.now(), value })
    );
  } catch {
    /* quota / private mode — ignore */
  }
};

/**
 * usePageContent — load editable CMS content for a page with instant
 * stale-while-revalidate semantics.
 *
 * Boot sequence:
 *   1. Synchronously hydrate from localStorage cache (if present + fresh).
 *      → returning visitors see the latest CMS content on first paint, NO flash.
 *   2. Kick off a background fetch from /api/page-content + /api/seo.
 *   3. When the fresh response arrives, merge into state and refresh cache.
 *
 * First-time visitors still see code defaults briefly (one-time, then cached).
 */
export const usePageContent = (pageType, pageId, defaults = {}) => {
  // Capture the cached value once per (pageType, pageId) so initial render
  // never shows defaults if we already have a previously-fetched copy.
  const initialContent = (() => {
    const cached = readCache(pageType, pageId);
    return cached ? { ...defaults, ...cached } : defaults;
  })();

  const [content, setContent] = useState(initialContent);
  // If we hydrated from cache, treat as "not loading" — the page is already
  // showing real CMS content and we'll silently revalidate in the background.
  const [loading, setLoading] = useState(!readCache(pageType, pageId));
  const lastKey = useRef(`${pageType}:${pageId}`);

  useEffect(() => {
    let cancelled = false;
    const key = `${pageType}:${pageId}`;
    // On page change, reset to (cache || defaults) so we never leak the prior
    // page's content while the new fetch is in flight.
    if (lastKey.current !== key) {
      const cached = readCache(pageType, pageId);
      setContent(cached ? { ...defaults, ...cached } : defaults);
      setLoading(!cached);
      lastKey.current = key;
    }

    const fetchContent = async () => {
      try {
        const [contentRes, seoRes] = await Promise.allSettled([
          axios.get(`${API}/page-content/${pageType}/${pageId}`),
          axios.get(`${API}/seo/${pageId}`),
        ]);

        if (cancelled) return;

        const pageData = contentRes.status === 'fulfilled' && contentRes.value.data?.content
          ? contentRes.value.data.content
          : {};

        const seoData = seoRes.status === 'fulfilled' && seoRes.value.data?.page_id
          ? seoRes.value.data
          : {};

        const merged = { ...pageData };
        if (seoData.meta_title) merged.seo = { ...merged.seo, title: seoData.meta_title };
        if (seoData.meta_description) merged.seo = { ...merged.seo, description: seoData.meta_description };
        if (seoData.meta_keywords) merged.seo = { ...merged.seo, keywords: seoData.meta_keywords };
        if (seoData.og_title) merged.seo = { ...merged.seo, og_title: seoData.og_title };
        if (seoData.og_description) merged.seo = { ...merged.seo, og_description: seoData.og_description };

        // Persist for next visit (instant hydration → no flash on reload).
        writeCache(pageType, pageId, merged);

        setContent({ ...defaults, ...merged });
      } catch {
        // Network/server error — keep cached/default content; do not clobber.
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchContent();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageType, pageId]);

  return { content, loading };
};
